/**
 * insightsIntel — pure functions that derive management-dashboard metrics
 * from the raw handovers / breakdowns / inspections / hazards / ppfs data
 * the Insights page already fetches from production.
 *
 * Kept as pure helpers (no React) so every widget stays cheap to memo and
 * every calc has a clear unit-testable shape.
 *
 * Josh's granular spec (Feb 22) maps 1:1 onto these functions:
 *   • computeKpis            → Command KPI strip
 *   • computeAlerts          → Alert Banner
 *   • computeRepeatFaults    → Repeat Fault Tracker
 *   • computeOutstanding     → Outstanding Work strip
 *   • computeCompliance      → Inspection Compliance
 *   • computeWearExtremes    → Wear Tracker
 *   • computeAssetHealth     → per-asset drill-in
 *   • buildAssetHistory      → click-through evidence trail for the drawer
 */

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
export function toDate(v) {
  if (!v) return null;
  try {
    // Backend sometimes serialises without the trailing Z — normalise.
    const s = String(v).replace(" ", "T");
    const d = new Date(s.endsWith("Z") || /[+-]\d\d:?\d\d$/.test(s) ? s : s + "Z");
    return isNaN(d) ? null : d;
  } catch { return null; }
}
export function daysBetween(a, b) {
  const da = toDate(a), db = toDate(b);
  if (!da || !db) return null;
  return Math.round((db - da) / 86400000);
}
export function hoursBetween(a, b) {
  const da = toDate(a), db = toDate(b);
  if (!da || !db) return null;
  return Math.max(0, (db - da) / 3600000);
}
function withinDays(v, days) {
  const d = toDate(v);
  if (!d) return false;
  return (Date.now() - d.getTime()) / 86400000 <= days;
}

// Cheap fault-signature: lowercase + strip noise so "Hydraulic hose leak"
// matches "hydraulic hose leaks" across multiple entries.
function faultSignature(bd) {
  const raw = (bd.complaint || bd.cause || bd.subcategory || bd.title || "").toString();
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\b(the|a|and|of|to|for|is|are|on|in|at)\b/g, " ")
    .replace(/s\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const CRIT = new Set(["critical", "high", "urgent", "p1", "1"]);
export function isCritical(rec) {
  const p = String(rec?.priority || rec?.severity || rec?.risk_level || "").toLowerCase();
  return CRIT.has(p);
}
export function isOpenBreakdown(bd) {
  const s = String(bd?.status || "").toLowerCase();
  if (["approved", "closed", "complete", "resolved", "signed"].some((k) => s.includes(k))) return false;
  if (bd?.back_in_service_time || bd?.finished_at || bd?.approved_at) return false;
  return true;
}
export function isDraftInspection(insp) {
  const s = String(insp?.status || "").toLowerCase();
  return s === "" || s === "draft" || s === "in_progress" || s === "open" || s === "pending";
}
export function isSubmittedInspection(insp) {
  const s = String(insp?.status || "").toLowerCase();
  return s.includes("submit") || s.includes("ready");
}
export function isApprovedInspection(insp) {
  const s = String(insp?.status || "").toLowerCase();
  return s.includes("approv") || s.includes("closed") || s.includes("signed") || s.includes("complete");
}
export function inspectionIssueCount(insp) {
  const map = insp?.items_state || {};
  return Object.values(map).filter((st) => String(st?.status || "").toLowerCase() === "issue").length;
}

// ---------------------------------------------------------------------------
// Command KPI strip
// ---------------------------------------------------------------------------
export function computeKpis(data, assets = []) {
  const { breakdowns = [], inspections = [], ppfs = [], hazards = [] } = data || {};
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);

  const openBreakdowns = breakdowns.filter(isOpenBreakdown);
  const criticalOpen = [...openBreakdowns, ...hazards].filter((r) => isCritical(r) && !isApprovedInspection(r));

  const assetSet = new Set(openBreakdowns.map((b) => b.asset_id).filter(Boolean));
  const assetsOffline = assetSet.size;

  const inspectionsToday = inspections.filter((i) => {
    const d = toDate(i.created_at || i.started_at);
    return d && d >= todayStart;
  });

  let breakdownHours = 0;
  for (const b of breakdowns) {
    const h = hoursBetween(b.breakdown_time || b.started_at, b.back_in_service_time || b.finished_at || new Date().toISOString());
    if (h != null && withinDays(b.breakdown_time || b.created_at, 30)) breakdownHours += h;
  }

  const outstandingRepairs = openBreakdowns.length + ppfs.filter((p) => !isApprovedInspection(p) && String(p.status || "").toLowerCase() !== "approved").length;

  return {
    openDefects: {
      value: openBreakdowns.length,
      subtitle: `${assetSet.size} asset${assetSet.size === 1 ? "" : "s"} affected`,
      records: openBreakdowns,
      drill: "breakdowns",
      href: "/breakdowns",
    },
    criticalFaults: {
      value: criticalOpen.length,
      subtitle: criticalOpen.length ? "Escalate now" : "All clear",
      records: criticalOpen,
      drill: "breakdowns",
      href: "/breakdowns",
    },
    assetsOffline: {
      value: assetsOffline,
      subtitle: assets.length ? `of ${assets.length} in fleet` : `unique diggers down`,
      records: [...assetSet].map((id) => ({ asset_id: id })),
      drill: "assets",
      href: "/assets",
    },
    inspectionsToday: {
      value: inspectionsToday.length,
      subtitle: `${inspectionsToday.filter(isApprovedInspection).length} approved`,
      records: inspectionsToday,
      drill: "inspections",
      href: "/inspections",
    },
    breakdownHours: {
      value: Math.round(breakdownHours),
      subtitle: "Last 30 days",
      records: breakdowns,
      drill: "breakdowns",
      href: "/breakdowns",
      unit: "h",
    },
    outstandingRepairs: {
      value: outstandingRepairs,
      subtitle: `${openBreakdowns.length} breakdowns · ${outstandingRepairs - openBreakdowns.length} Planning Requests`,
      records: [...openBreakdowns, ...ppfs.filter((p) => !isApprovedInspection(p))],
      drill: "ppfs",
      href: "/ppfs",
    },
  };
}

// ---------------------------------------------------------------------------
// Alert Banner
// ---------------------------------------------------------------------------
export function computeAlerts(data, assets = [], repeatWindowDays = 30) {
  const { breakdowns = [], inspections = [], ppfs = [], hazards = [] } = data || {};
  const now = Date.now();
  const alerts = [];

  // Critical defects open > 1 shift (~12 h)
  breakdowns.filter(isOpenBreakdown).filter(isCritical).forEach((b) => {
    const opened = toDate(b.breakdown_time || b.created_at);
    if (!opened) return;
    const ageH = (now - opened.getTime()) / 3600000;
    if (ageH >= 12) {
      alerts.push({
        id: `crit-${b.id || b._id}`,
        level: "critical",
        title: `${b.asset_id || "Asset"} · ${(b.complaint || b.cause || "critical breakdown").slice(0, 70)}`,
        subtitle: `Critical fault open for ${Math.round(ageH)} h — needs supervisor attention now`,
        drill: { kind: "breakdown", id: b.id || b._id, asset_id: b.asset_id },
      });
    }
  });

  // Repeat faults in the window
  const repeats = computeRepeatFaults(breakdowns, repeatWindowDays).filter((r) => r.count >= 2);
  repeats.slice(0, 5).forEach((r) => {
    alerts.push({
      id: `repeat-${r.asset_id}-${r.signature.slice(0, 20)}`,
      level: "high",
      title: `${r.asset_id} · "${r.headline}" recurring ${r.count}× in ${repeatWindowDays}d`,
      subtitle: `First seen ${r.firstAt?.toLocaleDateString() || "—"} · last ${r.lastAt?.toLocaleDateString() || "—"} — likely root cause not addressed`,
      drill: { kind: "asset", id: r.asset_id },
    });
  });

  // Wear ≥ 75%
  assets.forEach((a) => {
    const wear = getWearMap(a);
    Object.entries(wear).forEach(([position, pct]) => {
      const n = Number(pct);
      if (!Number.isFinite(n)) return;
      if (n >= 75) {
        alerts.push({
          id: `wear-${a.id}-${position}`,
          level: n >= 100 ? "critical" : "high",
          title: `${a.id} · ${position} at ${n}% wear`,
          subtitle: n >= 100 ? "Change-out due" : "Schedule replacement within the next 2 swings",
          drill: { kind: "asset", id: a.id },
        });
      }
    });
  });

  // Overdue inspections — draft/in-progress older than 3 days
  inspections.filter(isDraftInspection).forEach((i) => {
    const started = toDate(i.created_at || i.started_at);
    if (!started) return;
    const ageDays = (now - started.getTime()) / 86400000;
    if (ageDays >= 3) {
      alerts.push({
        id: `insp-overdue-${i.id || i._id}`,
        level: "medium",
        title: `${i.asset_id || "Asset"} · inspection open ${Math.round(ageDays)} days`,
        subtitle: "Draft hasn't been submitted for sign-off — chase or delete",
        drill: { kind: "inspection", id: i.id || i._id, asset_id: i.asset_id },
      });
    }
  });

  // Temp repairs (breakdowns with a "temp"/"temporary" tag in comments)
  breakdowns.forEach((b) => {
    if (isOpenBreakdown(b)) return;
    const notes = `${b.corrective_action || ""} ${b.comments || ""}`.toLowerCase();
    if (/(temp|temporary|band[- ]?aid|make[- ]?do|tie[- ]?wire)/.test(notes)) {
      const closedAt = toDate(b.back_in_service_time || b.updated_at);
      const ageDays = closedAt ? (now - closedAt.getTime()) / 86400000 : 0;
      if (ageDays >= 3) {
        alerts.push({
          id: `temp-${b.id || b._id}`,
          level: "medium",
          title: `${b.asset_id} · temporary repair not followed up (${Math.round(ageDays)} d)`,
          subtitle: (b.corrective_action || b.comments || "").slice(0, 90),
          drill: { kind: "breakdown", id: b.id || b._id, asset_id: b.asset_id },
        });
      }
    }
  });

  // Multiple breakdowns on same asset during same swing (last 7 days)
  const bySwing = new Map();
  breakdowns.forEach((b) => {
    if (!withinDays(b.created_at, 7)) return;
    const key = `${b.asset_id || "?"}-${b.shift || ""}-${(b.created_at || "").slice(0, 10)}`;
    if (!bySwing.has(key)) bySwing.set(key, []);
    bySwing.get(key).push(b);
  });
  for (const [key, bs] of bySwing.entries()) {
    if (bs.length >= 2) {
      const b0 = bs[0];
      alerts.push({
        id: `swing-multi-${key}`,
        level: "high",
        title: `${b0.asset_id} · ${bs.length} breakdowns in one swing`,
        subtitle: `${b0.shift || "shift"} · ${(b0.created_at || "").slice(0, 10)} — repeat failure risk`,
        drill: { kind: "asset", id: b0.asset_id },
      });
    }
  }

  // Outstanding parts (PPFs with sent-back or awaiting-parts flag) > 7 days
  ppfs.forEach((p) => {
    const isPartsWait = /(await|order|part)/.test(`${p.status || ""} ${p.sent_back_reason || ""}`.toLowerCase());
    if (!isPartsWait) return;
    const created = toDate(p.created_at || p.submitted_at);
    if (!created) return;
    const days = (now - created.getTime()) / 86400000;
    if (days >= 7) {
      alerts.push({
        id: `parts-${p.id || p._id}`,
        level: "medium",
        title: `${p.asset_id} · Planning Request awaiting parts for ${Math.round(days)} d`,
        subtitle: (p.fault || "").slice(0, 90),
        drill: { kind: "ppf", id: p.id || p._id, asset_id: p.asset_id },
      });
    }
  });

  return alerts;
}

// ---------------------------------------------------------------------------
// Repeat fault tracking
// ---------------------------------------------------------------------------
export function computeRepeatFaults(breakdowns, windowDays = 30) {
  const cutoff = Date.now() - windowDays * 86400000;
  const filtered = breakdowns.filter((b) => {
    const d = toDate(b.created_at || b.breakdown_time);
    return d && d.getTime() >= cutoff;
  });
  const groups = new Map();
  for (const b of filtered) {
    const sig = faultSignature(b);
    if (!sig || !b.asset_id) continue;
    const key = `${b.asset_id}::${sig}`;
    if (!groups.has(key)) {
      groups.set(key, {
        asset_id: b.asset_id,
        signature: sig,
        headline: (b.complaint || b.cause || b.subcategory || "Fault").slice(0, 80),
        occurrences: [],
        firstAt: null,
        lastAt: null,
        count: 0,
      });
    }
    const g = groups.get(key);
    const at = toDate(b.created_at || b.breakdown_time);
    g.occurrences.push({ id: b.id || b._id, at, complaint: b.complaint || b.cause, corrective: b.corrective_action, status: b.status });
    g.count = g.occurrences.length;
    if (at) {
      if (!g.firstAt || at < g.firstAt) g.firstAt = at;
      if (!g.lastAt || at > g.lastAt) g.lastAt = at;
    }
  }
  return [...groups.values()]
    .filter((g) => g.count >= 2)
    .sort((a, b) => b.count - a.count || (b.lastAt?.getTime() || 0) - (a.lastAt?.getTime() || 0));
}

// ---------------------------------------------------------------------------
// Outstanding work
// ---------------------------------------------------------------------------
export function computeOutstanding(data) {
  const { breakdowns = [], inspections = [], ppfs = [], handovers = [] } = data || {};
  const defectsAwaitingRepair = breakdowns.filter(isOpenBreakdown).length;
  const reportsAwaiting = [
    ...inspections.filter(isSubmittedInspection),
    ...ppfs.filter((p) => {
      const s = String(p.status || "").toLowerCase();
      return s.includes("submit") || s.includes("pending") || s.includes("await");
    }),
  ].length;
  const partsAwaiting = ppfs.filter((p) => /(await|order|part)/.test(`${p.status || ""} ${p.sent_back_reason || ""}`.toLowerCase())).length;
  const tempRepairs = breakdowns.filter((b) => /(temp|temporary|band[- ]?aid|tie[- ]?wire)/.test(`${b.corrective_action || ""} ${b.comments || ""}`.toLowerCase())).length;
  const carriedOver = handovers.filter((h) => /(carry|carried|outstanding|left over|to be)/.test(`${h.summary || ""} ${h.tasks_outstanding || ""}`.toLowerCase())).length;
  return { defectsAwaitingRepair, reportsAwaiting, partsAwaiting, tempRepairs, carriedOver };
}

// ---------------------------------------------------------------------------
// Inspection compliance
// ---------------------------------------------------------------------------
export function computeCompliance(inspections) {
  let onTime = 0, overdue = 0, drafts = 0, issuesFound = 0;
  const now = Date.now();
  for (const i of inspections || []) {
    if (isApprovedInspection(i)) {
      const created = toDate(i.created_at || i.started_at);
      const closed = toDate(i.approved_at || i.updated_at);
      if (created && closed) {
        const hours = (closed - created) / 3600000;
        if (hours <= 24) onTime++; else overdue++;
      } else {
        onTime++;
      }
    } else if (isDraftInspection(i)) {
      drafts++;
      const created = toDate(i.created_at || i.started_at);
      if (created && (now - created.getTime()) / 86400000 > 3) overdue++;
    }
    issuesFound += inspectionIssueCount(i);
  }
  const total = inspections?.length || 0;
  const missed = 0; // no scheduled-inspection endpoint yet — hidden from UI
  return { onTime, overdue, missed, drafts, issuesFound, total };
}

// ---------------------------------------------------------------------------
// Wear extremes
// ---------------------------------------------------------------------------
export function getWearMap(asset) {
  const w = asset?.current_get || {};
  const out = {};
  ["tooth", "lip_shroud", "wing_shroud"].forEach((group) => {
    const g = w[group] || {};
    Object.entries(g).forEach(([k, v]) => {
      const n = Number(v);
      if (Number.isFinite(n)) out[`${asset.id || asset.asset_id} · ${k}`] = n;
    });
  });
  return out;
}
export function computeWearExtremes(assets = []) {
  const rows = [];
  for (const a of assets) {
    const wear = a?.current_get || {};
    ["tooth", "lip_shroud", "wing_shroud"].forEach((group) => {
      const g = wear[group] || {};
      Object.entries(g).forEach(([position, pct]) => {
        const n = Number(pct);
        if (!Number.isFinite(n)) return;
        rows.push({
          asset_id: a.id || a.asset_id,
          asset_name: a.name || a.asset_name,
          group,
          position,
          pct: n,
          updated_at: wear.updated_at,
          updated_by: wear.updated_by_name,
        });
      });
    });
  }
  rows.sort((a, b) => b.pct - a.pct);
  return {
    worst: rows.slice(0, 8),
    approaching: rows.filter((r) => r.pct >= 50 && r.pct < 100),
  };
}

// ---------------------------------------------------------------------------
// Per-asset health drill-in
// ---------------------------------------------------------------------------
export function computeAssetHealth(assetId, data, assets = []) {
  if (!assetId) return null;
  const { breakdowns = [], inspections = [], ppfs = [], hazards = [] } = data || {};
  const bAll = breakdowns.filter((b) => b.asset_id === assetId);
  const iAll = inspections.filter((i) => i.asset_id === assetId);
  const pAll = ppfs.filter((p) => p.asset_id === assetId);
  const hAll = hazards.filter((h) => h.asset_id === assetId);
  const asset = assets.find((a) => (a.id || a.asset_id) === assetId) || null;
  const openDefects = bAll.filter(isOpenBreakdown);
  const criticalDefects = openDefects.filter(isCritical);
  const repeatFaults = computeRepeatFaults(bAll, 90);
  const inspFailures = iAll.filter((i) => inspectionIssueCount(i) > 0);
  const lastInsp = iAll.map((i) => toDate(i.updated_at || i.approved_at || i.created_at)).filter(Boolean).sort((a, b) => b - a)[0] || null;
  const availability = openDefects.length === 0 ? "ready" : criticalDefects.length ? "down" : "at-risk";
  return {
    asset,
    assetId,
    availability,
    openDefects,
    criticalDefects,
    repeatFaults,
    inspFailures,
    inspectionCount: iAll.length,
    breakdownCount: bAll.length,
    ppfCount: pAll.length,
    hazardCount: hAll.length,
    lastInspection: lastInsp,
    history: buildAssetHistory(assetId, data),
  };
}
export function buildAssetHistory(assetId, data) {
  const events = [];
  (data?.inspections || []).filter((i) => i.asset_id === assetId).forEach((i) => events.push({
    kind: "inspection", at: toDate(i.updated_at || i.created_at), id: i.id || i._id,
    label: i.template_name || i.title || "Inspection",
    status: i.status,
  }));
  (data?.breakdowns || []).filter((b) => b.asset_id === assetId).forEach((b) => events.push({
    kind: "breakdown", at: toDate(b.created_at || b.breakdown_time), id: b.id || b._id,
    label: b.complaint || b.cause || "Breakdown",
    status: b.status,
    critical: isCritical(b),
  }));
  (data?.ppfs || []).filter((p) => p.asset_id === assetId).forEach((p) => events.push({
    kind: "ppf", at: toDate(p.created_at), id: p.id || p._id,
    label: p.fault || "Planning Request",
    status: p.status,
    priority: p.priority,
  }));
  (data?.hazards || []).filter((h) => h.asset_id === assetId).forEach((h) => events.push({
    kind: "hazard", at: toDate(h.created_at || h.reported_at), id: h.id || h._id,
    label: h.description || h.summary || "Hazard",
    status: h.status,
  }));
  return events.filter((e) => e.at).sort((a, b) => b.at - a.at).slice(0, 40);
}

// ---------------------------------------------------------------------------
// Filter application
// ---------------------------------------------------------------------------
export function applyFilters(data, filters = {}) {
  const { assetType, assetId, crew, shift, supervisor, status, dateFrom, dateTo } = filters;
  const inRange = (v) => {
    if (!dateFrom && !dateTo) return true;
    const d = toDate(v);
    if (!d) return true;
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo)   { const to = new Date(dateTo); to.setHours(23, 59, 59, 999); if (d > to) return false; }
    return true;
  };
  const passRecord = (rec, opts = {}) => {
    if (assetId && rec.asset_id !== assetId) return false;
    if (assetType && String(rec.asset_type || rec.category || "").toLowerCase() !== assetType.toLowerCase()) return false;
    if (crew && String(rec.crew_name || rec.crew || rec.assigned_crew_name || "").toLowerCase() !== crew.toLowerCase()) return false;
    if (shift && String(rec.shift || rec.from_shift || "").toLowerCase() !== shift.toLowerCase()) return false;
    if (supervisor && String(rec.supervisor_name || rec.assigned_supervisor_name || "").toLowerCase() !== supervisor.toLowerCase()) return false;
    if (status && String(rec.status || "").toLowerCase() !== status.toLowerCase()) return false;
    if (!inRange(rec.created_at || rec.reported_at || rec.breakdown_time || rec.date)) return false;
    return true;
  };
  return {
    handovers:   (data.handovers   || []).filter(passRecord),
    breakdowns:  (data.breakdowns  || []).filter(passRecord),
    inspections: (data.inspections || []).filter(passRecord),
    hazards:     (data.hazards     || []).filter(passRecord),
    ppfs:        (data.ppfs        || []).filter(passRecord),
  };
}
